import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

// On every boot, restore the saved token onto axios so requests fired
// before <AuthProvider> mounts (e.g. inline page-load fetches) still
// authenticate. Without this, the very first /auth/me race can flap.
const _bootToken = (() => {
  try { return localStorage.getItem("spicy_token"); } catch (_) { return null; }
})();
if (_bootToken) {
  api.defaults.headers.common["Authorization"] = `Bearer ${_bootToken}`;
}

// Auto-handle expired sessions: when the backend says the JWT is dead
// we clear it, surface a friendly toast, and bounce to login so the user
// isn't stuck in a loop of "Token expired" errors after a long session.
let _redirecting = false;
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || "";
    const isExpired = status === 401 && /token expired|invalid token|not authenticated/i.test(String(detail));
    if (isExpired && !_redirecting) {
      _redirecting = true;
      try {
        localStorage.removeItem("spicy_token");
        delete api.defaults.headers.common["Authorization"];
      } catch (_) {}
      // Avoid infinite redirect loops on the auth pages themselves.
      const path = window.location.pathname;
      if (!path.startsWith("/admin/login") && path !== "/login") {
        // Tiny delay so any in-flight toast can render before navigation.
        setTimeout(() => {
          // Use full reload so React state doesn't carry over a dead user.
          window.location.href = path.startsWith("/admin")
            ? "/admin/login"
            : "/";
        }, 300);
      }
    }
    return Promise.reject(err);
  },
);

export function wsURL() {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws`;
}

export function resolveAsset(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export function formatError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
